/**
 * Suivi de production dialogs: an étape (add / edit, with its slot and live conflict
 * check), a quantity saisie (live totals, never overwrites), and the motif asked to
 * stop a step or to end it with fewer pieces than asked.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccessTime as SlotIcon,
  AddTask as SaisieIcon,
  Close as CloseIcon,
  EastRounded as AfterIcon,
  ErrorOutline as ErrIcon,
  History as HistIcon,
  PauseCircleOutline as AttenteIcon,
  PrecisionManufacturing as EtapeIcon,
  TaskAlt as DoneIcon,
  WarningAmberRounded as WarnIcon,
} from '@mui/icons-material';
import { comptoirProductionAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useAuth } from '../../../contexts/AuthContext';
import { useComptoirEtape, useComptoirSuiviMeta, useComptoirSuiviService } from '../../../hooks/queries/useComptoir';
import {
  ComptoirEtape, ComptoirEtapeInput, ComptoirSaisieInput, ComptoirSaisieType, ComptoirSlotCheck,
} from '../../../types/comptoir';
import { COLOR, NumberField, fDate, num, primaryBtnSx, r2 } from '../ui';
import PlansButton from '../../../components/plans/PlansButton';

/* ---------------- shared helpers ---------------- */
export const STATUT_COLOR: Record<string, string> = {
  a_faire: '#00897B',
  en_cours: '#1E88E5',
  partiel: '#7B1FA2',
  bloque: '#D32F2F',
  termine: '#2E7D32',
};
const RED = '#D32F2F';
const ORANGE = '#ED6C02';

/** 24 · 2,5 — quantities without useless decimals. */
export const fq = (v: number | string | null | undefined) =>
  Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export const fDur = (m: number) => {
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h} h${r ? ` ${String(r).padStart(2, '0')}` : ''}` : `${r} min`;
};
const toMin = (t: string) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const toHHMM = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const DAYS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
export const slotLabel = (e: Pick<ComptoirEtape, 'date' | 'heure_debut' | 'heure_fin' | 'duree_min'>) =>
  e.date && e.heure_debut
    ? `${DAYS[new Date(`${e.date}T00:00:00`).getDay()]} ${fDate(e.date)} · ${e.heure_debut}–${e.heure_fin}`
    : `À planifier · ${fDur(e.duree_min)}`;

export const StatutChip: React.FC<{ e: Pick<ComptoirEtape, 'statut' | 'statut_label'> }> = ({ e }) => {
  const c = STATUT_COLOR[e.statut] || '#607D8B';
  return (
    <Chip label={e.statut_label} size="small"
      sx={{ height: 22, fontWeight: 700, color: '#fff', bgcolor: c, '& .MuiChip-label': { px: 1 } }} />
  );
};

/** conformes / demandée bar; the pieces à refaire show in orange after the green part. */
export const ProdBar: React.FC<{ e: ComptoirEtape; compact?: boolean }> = ({ e, compact }) => {
  if (!e.defined) return <Chip size="small" variant="outlined" label="Quantité à définir" sx={{ color: ORANGE, borderColor: ORANGE }} />;
  if (e.inconnue) return <Chip size="small" variant="outlined" label="Situation à saisir" sx={{ color: ORANGE, borderColor: ORANGE }} />;
  const dem = num(e.demandee), conf = num(e.conformes), ref = num(e.a_refaire);
  const w2 = ref ? Math.min(100 - e.pct, Math.max(2, Math.round((ref / dem) * 100))) : 0;
  return (
    <Box sx={{ minWidth: compact ? 110 : 160 }}>
      <Box sx={{ position: 'relative', height: 7, borderRadius: 4, bgcolor: (t) => alpha(t.palette.text.primary, 0.08), overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${e.pct}%`, bgcolor: '#2E7D32' }} />
        {w2 > 0 && <Box sx={{ position: 'absolute', left: `${e.pct}%`, top: 0, bottom: 0, width: `${w2}%`, bgcolor: ORANGE }} />}
      </Box>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        <b>{fq(conf)}</b>/{fq(dem)}
        {!compact && ` · reste ${fq(e.restante)}`}
        {ref > 0 && <Box component="b" sx={{ color: ORANGE }}> · {fq(ref)} à refaire</Box>}
      </Typography>
    </Box>
  );
};

const Head: React.FC<{ icon: React.ReactNode; title: string; sub?: string; id: string; onClose: () => void; color?: string }> = ({ icon, title, sub, id, onClose, color = COLOR }) => (
  <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
    <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(color, 0.12), color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography id={id} variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
    </Box>
    <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
  </Box>
);

const Msg: React.FC<{ kind: 'error' | 'warn' | 'ok'; children: React.ReactNode }> = ({ kind, children }) => {
  const c = kind === 'error' ? RED : kind === 'warn' ? ORANGE : '#2E7D32';
  const Icon = kind === 'error' ? ErrIcon : kind === 'warn' ? WarnIcon : DoneIcon;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start"
      sx={{ p: 1.25, mb: 1, borderRadius: 1.5, border: `1px solid ${alpha(c, 0.4)}`, bgcolor: alpha(c, 0.06) }}>
      <Icon sx={{ color: c, fontSize: 20, mt: '1px' }} />
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
};

/* ---------------- Étape (add / edit) ---------------- */
type EtapeForm = {
  service_id: string; travail_id: string; qte: string; designation: string; ordre: string; depend: boolean;
  machine_id: string; responsable_id: string; date: string; debut: string; fin: string; duree: string; note: string;
};

export const EtapeDialog: React.FC<{
  open: boolean;
  etape: ComptoirEtape | null;
  serviceId: number | null;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (e: ComptoirEtape, created: boolean) => void;
}> = ({ open, etape, serviceId, canEdit, onClose, onSaved }) => {
  const { data: meta } = useComptoirSuiviMeta(open);
  const [f, setF] = useState<EtapeForm>({} as EtapeForm);
  const [check, setCheck] = useState<ComptoirSlotCheck>({ conflits: [], avertissements: [] });
  const [checking, setChecking] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [hint, setHint] = useState('');
  const [saving, setSaving] = useState(false);
  const sid = f.service_id ? Number(f.service_id) : null;
  const { data: card } = useComptoirSuiviService(sid, open);
  const services = useMemo(() => {
    const list = meta?.services || [];
    // an étape of a closed / delivered service still shows its own service
    if (etape && !list.some((s) => s.id === etape.service_id) && card) {
      return [{ id: card.id, reference: card.reference, titre: card.titre, client_nom: card.client_nom, deadline: card.deadline, nb_etapes: card.etapes.length, travaux: [] }, ...list];
    }
    return list;
  }, [meta, etape, card]);
  const svc = services.find((s) => s.id === sid);
  const travail = svc?.travaux.find((t) => String(t.id) === f.travail_id);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false); setServerErr(''); setHint(''); setCheck({ conflits: [], avertissements: [] });
    if (etape) {
      setF({
        service_id: String(etape.service_id), travail_id: etape.travail_id ? String(etape.travail_id) : '',
        qte: etape.qte_demandee != null ? String(etape.qte_demandee).replace('.', ',') : '', designation: etape.designation,
        ordre: String(etape.ordre), depend: etape.depend_precedente,
        machine_id: etape.machine ? String(etape.machine.id) : '', responsable_id: etape.responsable ? String(etape.responsable.id) : '',
        date: etape.date || '', debut: etape.heure_debut || '08:00', fin: etape.heure_fin || '', duree: String(etape.duree_min), note: etape.note || '',
      });
    } else {
      setF({
        service_id: serviceId ? String(serviceId) : '', travail_id: '', qte: '', designation: '', ordre: '1', depend: false,
        machine_id: '', responsable_id: '', date: '', debut: '08:00', fin: '08:30', duree: '30', note: '',
      });
    }
  }, [open, etape, serviceId]);

  // new étape: last in its service, after the previous one
  useEffect(() => {
    if (!open || etape || !svc) return;
    const n = svc.nb_etapes + 1;
    setF((x) => ({ ...x, ordre: String(n), depend: n > 1 }));
  }, [open, etape, svc]);

  const duree = Math.round(num(f.duree));
  const debut = toMin(f.debut);
  const setDebut = (v: string) => {
    const d = toMin(v);
    setF((x) => ({ ...x, debut: v, fin: d != null && duree > 0 ? toHHMM(d + duree) : x.fin }));
  };
  const setDuree = (v: string) => {
    const n = Math.round(num(v));
    setF((x) => ({ ...x, duree: v, fin: debut != null && n > 0 ? toHHMM(debut + n) : x.fin }));
  };
  const setFin = (v: string) => {
    const e = toMin(v);
    setF((x) => ({ ...x, fin: v, duree: debut != null && e != null && e > debut ? String(e - debut) : x.duree }));
  };

  const slotParams = () => ({
    service_id: sid as number, travail_id: f.travail_id ? Number(f.travail_id) : null, ordre: Number(f.ordre) || 1,
    depend_precedente: f.depend, machine_id: f.machine_id ? Number(f.machine_id) : null,
    responsable_id: f.responsable_id ? Number(f.responsable_id) : null, date: f.date || null,
    heure_debut: f.date ? f.debut : null, duree_min: duree > 0 ? duree : 30, etape_id: etape?.id ?? null,
  });

  // live conflicts, as the form changes
  useEffect(() => {
    if (!open || !sid) return;
    let alive = true;
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await comptoirProductionAPI.conflits(slotParams());
        if (alive) setCheck(r);
      } catch { /* the save will tell */ } finally { if (alive) setChecking(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sid, f.travail_id, f.ordre, f.depend, f.machine_id, f.responsable_id, f.date, f.debut, f.duree]);

  const afterPrev = () => {
    setHint('');
    const ordre = Number(f.ordre) || 1;
    const before = (card?.etapes || []).filter((x) => x.id !== etape?.id && x.ordre < ordre);
    const same = before.filter((x) => String(x.travail_id || '') === f.travail_id);
    const p = (same.length ? same : before).slice(-1)[0];
    if (!p) { setHint("Il n'y a pas d'étape avant celle-ci."); return; }
    if (!p.date || !p.heure_fin) { setHint(`L'étape précédente « ${p.designation} » n'est pas encore planifiée.`); return; }
    setF((x) => ({ ...x, date: p.date as string }));
    setDebut(p.heure_fin);
  };
  const freeSlot = async () => {
    setHint('');
    if (!sid) return;
    try {
      const r = await comptoirProductionAPI.creneauLibre(slotParams());
      if (!r.date || !r.heure_debut) { setHint('Aucun créneau libre dans les 45 prochains jours.'); return; }
      setF((x) => ({ ...x, date: r.date as string }));
      setDebut(r.heure_debut);
    } catch (e: any) { setHint(extractErrorMessage(e)); }
  };

  const errors: Record<string, string> = {};
  if (!sid) errors.service = 'Choisissez le service.';
  if (!f.designation?.trim()) errors.designation = "Indiquez l'étape (ex : Découpe, Pliage).";
  if (!(duree > 0)) errors.duree = 'Indiquez la durée estimée en minutes (ex : 30).';
  if (f.date && debut == null) errors.debut = "Indiquez l'heure de début.";
  if (f.qte && !(num(f.qte) > 0)) errors.qte = 'La quantité doit être positive.';
  const show = (k: string) => (submitted ? errors[k] : undefined);

  const save = async (forcer: boolean) => {
    setSubmitted(true); setServerErr('');
    if (Object.keys(errors).length) return;
    const data: ComptoirEtapeInput = {
      service_id: sid as number, travail_id: f.travail_id ? Number(f.travail_id) : null, designation: f.designation.trim(),
      ordre: Number(f.ordre) || null, depend_precedente: f.depend, qte_demandee: f.qte ? r2(num(f.qte)) : null,
      machine_id: f.machine_id ? Number(f.machine_id) : null, responsable_id: f.responsable_id ? Number(f.responsable_id) : null,
      date: f.date || null, heure_debut: f.date ? f.debut : null, duree_min: duree, note: f.note.trim() || null, forcer,
    };
    setSaving(true);
    try {
      const e = etape ? await comptoirProductionAPI.updateEtape(etape.id, data) : await comptoirProductionAPI.createEtape(data);
      onSaved(e, !etape);
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  const conflict = check.conflits.length > 0;
  const nbMax = (svc?.nb_etapes || 0) + (etape ? 0 : 1);
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="etape-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="etape-title" icon={<EtapeIcon />} onClose={onClose}
        title={etape ? `${card?.reference || ''} · ${etape.designation}` : 'Nouvelle étape de fabrication'}
        sub={etape ? `Étape ${etape.ordre} · ${etape.statut_label}` : 'Une étape = une opération sur une machine (découpe, pliage, soudage…)'} />
      {checking && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { bgcolor: COLOR }, bgcolor: alpha(COLOR, 0.1) }} />}
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            {/* searchable: type a reference, a client or words of the title */}
            <Autocomplete
              options={services} disabled={!!etape} autoHighlight openOnFocus
              value={services.find((s) => String(s.id) === String(f.service_id)) || null}
              onChange={(_, s) => setF({ ...f, service_id: s ? String(s.id) : '', travail_id: '' })}
              getOptionLabel={(s) => `${s.reference} ${s.titre}${s.client_nom ? ` · ${s.client_nom}` : ''}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(opts, { inputValue }) => {
                const words = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
                return opts.filter((s) => {
                  const hay = `${s.reference} ${s.titre} ${s.client_nom || ''}`.toLowerCase();
                  return words.every((w) => hay.includes(w));
                });
              }}
              renderOption={(props, s) => (
                <li {...props} key={s.id}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, mr: 1 }}>{s.reference}</Box>
                  {s.titre}{s.client_nom ? ` · ${s.client_nom}` : ''}
                </li>
              )}
              noOptionsText="Aucun service / projet"
              renderInput={(params) => (
                <TextField {...params} label="Service / projet" required placeholder="Référence, client ou titre…"
                  error={!!show('service')} helperText={show('service') || ' '} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField select label="Travail concerné" fullWidth value={f.travail_id || ''} disabled={!svc}
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
              onChange={(e) => setF({ ...f, travail_id: e.target.value })} helperText=" ">
              <MenuItem value="">Tout le service</MenuItem>
              {(svc?.travaux || []).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.label} ({fq(t.quantite)} {t.unite})</MenuItem>)}
              {etape?.travail_id && !svc?.travaux.some((t) => t.id === etape.travail_id) && (
                <MenuItem value={String(etape.travail_id)}>{etape.travail_label}</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <Autocomplete freeSolo options={meta?.etapes || []} value={f.designation || ''} inputValue={f.designation || ''}
              onInputChange={(_, v, reason) => { if (reason !== 'reset' || v) setF((x) => ({ ...x, designation: v })); }}
              renderInput={(p) => <TextField {...p} label="Étape" required placeholder="Découpe, pliage, soudage…"
                error={!!show('designation')} helperText={show('designation') || ' '} />} />
          </Grid>
          <Grid item xs={6} md={3}>
            <NumberField label="Quantité à réaliser" fullWidth value={f.qte || ''} onChange={(v) => setF({ ...f, qte: v })}
              placeholder={travail ? fq(travail.quantite) : 'À préciser'} unit={travail?.unite} InputLabelProps={{ shrink: true }}
              error={!!show('qte')}
              helperText={show('qte') || (travail ? 'Vide = quantité du travail' : 'Ex : 200 pièces découpées pour 100 supports')} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField select label="Ordre de fabrication" fullWidth value={f.ordre || '1'}
              onChange={(e) => setF({ ...f, ordre: e.target.value, depend: Number(e.target.value) > 1 && f.depend })} helperText=" ">
              {Array.from({ length: Math.max(1, nbMax) }, (_, i) => <MenuItem key={i + 1} value={String(i + 1)}>Étape {i + 1}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel sx={{ mt: -1.5 }}
              control={<Checkbox checked={!!f.depend} disabled={(Number(f.ordre) || 1) <= 1} onChange={(e) => setF({ ...f, depend: e.target.checked })} />}
              label="Commence après la fin de l'étape précédente (n'utilise que ses pièces conformes)" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField select label="Machine" fullWidth value={f.machine_id || ''} onChange={(e) => setF({ ...f, machine_id: e.target.value })} helperText=" "
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">— Aucune —</MenuItem>
              {(meta?.machines || []).map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, mr: 1 }}>{m.code}</Box>
                  {m.name}{m.atelier ? ` · ${m.atelier}` : ''}
                  {!m.disponible && <Box component="span" sx={{ color: RED, ml: 1, fontSize: 12 }}>({m.status_label})</Box>}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField select label="Responsable de l'étape" fullWidth value={f.responsable_id || ''}
              onChange={(e) => setF({ ...f, responsable_id: e.target.value })} helperText=" "
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">— Non affecté —</MenuItem>
              {(meta?.responsables || []).map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.full_name}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(COLOR, 0.03) }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <SlotIcon sx={{ color: COLOR }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Créneau</Typography>
            <Button size="small" startIcon={<AfterIcon />} onClick={afterPrev} disabled={!sid || (Number(f.ordre) || 1) <= 1}>Juste après l'étape précédente</Button>
            <Button size="small" startIcon={<SlotIcon />} onClick={freeSlot} disabled={!sid || !(duree > 0)}>Prochain créneau libre</Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={f.date || ''}
                onChange={(e) => setF({ ...f, date: e.target.value })} helperText="Vide = en attente de planification" />
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <TextField label="Début" type="time" fullWidth InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                value={f.debut || ''} onChange={(e) => setDebut(e.target.value)} error={!!show('debut')} helperText={show('debut') || ' '} />
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <TextField label="Fin" type="time" fullWidth InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                value={f.fin || ''} onChange={(e) => setFin(e.target.value)} helperText="Calculée, modifiable" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <NumberField label="Durée estimée" required fullWidth unit="min" value={f.duree || ''} onChange={setDuree}
                error={!!show('duree')} helperText={show('duree') || ' '} />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {(meta?.durees || []).map((m) => (
              <Chip key={m} label={fDur(m)} size="small" clickable onClick={() => setDuree(String(m))}
                variant={duree === m ? 'filled' : 'outlined'}
                sx={duree === m ? { bgcolor: COLOR, color: '#fff' } : undefined} />
            ))}
          </Stack>
          {hint && <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>{hint}</Typography>}
        </Paper>

        <TextField label="Note" fullWidth value={f.note || ''} onChange={(e) => setF({ ...f, note: e.target.value })} sx={{ mt: 2 }} />

        <Box sx={{ mt: 2 }}>
          {check.conflits.map((c, i) => <Msg key={`c${i}`} kind="error">{c.t}</Msg>)}
          {check.avertissements.map((w, i) => <Msg key={`w${i}`} kind="warn">{w}</Msg>)}
          {conflict && !canEdit && (
            <Typography variant="body2" color="text.secondary">Seul un responsable peut enregistrer malgré un conflit : changez le créneau, ou laissez la date vide.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        {conflict && canEdit && (
          <Button variant="outlined" color="error" onClick={() => save(true)} disabled={saving}>Enregistrer malgré le conflit</Button>
        )}
        <Button variant="contained" onClick={() => save(false)} disabled={saving || conflict} sx={{ ...primaryBtnSx, minWidth: 150 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>
          {etape ? 'Enregistrer' : "Ajouter l'étape"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Saisie de la quantité réalisée ---------------- */
const KINDS: Record<ComptoirSaisieType, string> = {
  production: 'Production', reprise: 'Pièces refaites', initial: 'Situation actuelle', correction: 'Correction',
};
const APRES: [ComptoirSaisieInput['apres'], string][] = [
  ['continuer', 'Je continue'], ['pause', 'Pause · partiellement réalisée'], ['attente', 'Arrêt · en attente'],
];
const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export const SaisieDialog: React.FC<{
  open: boolean;
  etapeId: number | null;
  kind?: ComptoirSaisieType;
  reference?: string;
  canEdit: boolean;
  /** plans techniques reaching this étape: « Plans (n) » next to the machine */
  plans?: number;
  onClose: () => void;
  onSaved: (e: ComptoirEtape) => void;
}> = ({ open, etapeId, kind, reference, canEdit, plans, onClose, onSaved }) => {
  const { user } = useAuth();
  const { data: e, isFetching } = useComptoirEtape(open ? etapeId : null);
  const { data: meta } = useComptoirSuiviMeta(open);
  const [k, setK] = useState<ComptoirSaisieType>('production');
  const [x, setX] = useState('');
  const [nc, setNc] = useState('');
  const [motif, setMotif] = useState('');
  const [obs, setObs] = useState('');
  const [ts, setTs] = useState(nowLocal());
  const [dem, setDem] = useState('');
  const [apres, setApres] = useState<ComptoirSaisieInput['apres']>('continuer');
  const [attMotif, setAttMotif] = useState('');
  const [attDetail, setAttDetail] = useState('');
  const [ov, setOv] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [initFor, setInitFor] = useState<number | null>(null);

  const kinds = useMemo(() => {
    if (!e) return [] as ComptoirSaisieType[];
    const out: ComptoirSaisieType[] = ['production'];
    if (num(e.a_refaire) > 0) out.push('reprise');
    if (!e.nb_saisies && e.statut !== 'a_faire') out.push('initial');
    if (e.nb_saisies && canEdit) out.push('correction');
    return out;
  }, [e, canEdit]);

  useEffect(() => { if (!open) setInitFor(null); }, [open]);
  useEffect(() => {
    if (!open || !e || initFor === e.id) return;
    setInitFor(e.id);
    const wanted = kind && kinds.includes(kind) ? kind : e.inconnue ? 'initial' : 'production';
    setK(wanted); setX(''); setNc(''); setMotif(''); setObs(''); setTs(nowLocal()); setOv(false);
    setDem(e.defined ? '' : e.travail_quantite ? String(e.travail_quantite).replace('.', ',') : '');
    setApres(e.statut === 'partiel' ? 'pause' : e.statut === 'bloque' ? 'attente' : 'continuer');
    setAttMotif(e.attente?.motif || meta?.motifs_attente[0] || '');
    setAttDetail(e.attente?.detail || '');
    setSubmitted(false); setServerErr('');
  }, [open, e, kind, kinds, initFor, meta]);

  /* the same rules as the backend, to show the result before saving */
  const ev = useMemo(() => {
    const errs: string[] = [], warns: string[] = [];
    if (!e) return { errs, warns, real: 0, conf: 0, refaire: 0, reste: 0, demandee: 0, over: false, done: false, typed: false };
    const demandee = e.defined ? num(e.demandee) : r2(num(dem));
    const vx = r2(num(x)), vnc = k === 'reprise' ? 0 : r2(num(nc));
    let real = num(e.realisee), ncs = num(e.non_conformes), rep = num(e.refaites);
    if (k === 'reprise') rep += vx; else { real += vx; ncs += vnc; }
    real = r2(real); ncs = r2(ncs); rep = r2(rep);
    const conf = r2(real - ncs + rep), refaire = r2(Math.max(0, ncs - rep)), reste = r2(Math.max(0, demandee - conf));
    let over = false;
    if (!(demandee > 0)) errs.push('Indiquez la quantité demandée pour cette étape.');
    if (k === 'correction') {
      if (!vx && !vnc) errs.push('Indiquez la correction (ex : -5).');
      if (!motif.trim()) errs.push('Indiquez le motif de la correction.');
      if (real < 0 || ncs < 0 || ncs > real) errs.push('La correction donne des quantités incohérentes.');
    } else if (k === 'initial') {
      if (x.trim() === '' || vx < 0) errs.push("Indiquez la quantité déjà réalisée (0 si rien n'est fait).");
    } else if (!(vx > 0)) errs.push(k === 'reprise' ? 'Indiquez le nombre de pièces refaites.' : 'Indiquez la quantité réalisée maintenant.');
    if (k === 'production' || k === 'initial') {
      if (vnc > vx) errs.push('Les non conformes ne peuvent pas dépasser la quantité réalisée.');
      if (vnc > 0 && !motif.trim()) errs.push('Indiquez le motif de reprise des pièces non conformes.');
    }
    if (k === 'reprise' && vx > num(e.a_refaire)) errs.push(`Il n'y a que ${fq(e.a_refaire)} pièce(s) à refaire.`);
    if (demandee > 0 && real > demandee && k !== 'correction') {
      over = true;
      if (ov) warns.push(`Dépassement autorisé : ${fq(real)} réalisées pour ${fq(demandee)} demandées.`);
      else errs.push(`Le total réalisé (${fq(real)}) dépasserait la quantité demandée (${fq(demandee)}).`);
    }
    const dp = e.disponible;
    if (k === 'production' && dp.depuis && dp.limite != null && real > num(dp.limite)) {
      over = true;
      const t = `Seulement ${fq(dp.limite)} pièce(s) disponibles depuis l'étape précédente « ${dp.depuis.designation} » (${fq(dp.depuis.conformes)} conformes).`;
      if (ov) warns.push(`Dépassement autorisé — ${t}`);
      else errs.push(t + (canEdit ? ' Cochez l’autorisation si c’est voulu.' : ' Demandez au responsable production.'));
    }
    if (k === 'production' && dp.inconnue && dp.depuis) {
      warns.push(`L'étape précédente « ${dp.depuis.designation} » n'a pas encore de quantités saisies : impossible de vérifier les pièces disponibles.`);
    }
    if (apres === 'attente' && !attMotif) errs.push("Choisissez le motif de l'arrêt.");
    if (ts && new Date(ts).getTime() > Date.now() + 5 * 60000) errs.push('La date de la saisie ne peut pas être dans le futur.');
    const typed = x.trim() !== '' || num(nc) !== 0;
    return { errs, warns, real, conf, refaire, reste, demandee, over, done: demandee > 0 && conf >= demandee, typed };
  }, [e, k, x, nc, motif, dem, ov, apres, attMotif, ts, canEdit]);

  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (!e || ev.errs.length) return;
    setSaving(true);
    try {
      const r = await comptoirProductionAPI.saisir(e.id, {
        type: k, qte: r2(num(x)), non_conformes: k === 'reprise' ? 0 : r2(num(nc)), motif: motif.trim() || null,
        observations: obs.trim() || null, saisie_le: ts ? new Date(ts).toISOString() : null, apres,
        attente_motif: apres === 'attente' ? attMotif : null, attente_detail: apres === 'attente' ? attDetail.trim() || null : null,
        depassement: ov, qte_demandee: e.defined ? null : r2(num(dem)),
      });
      onSaved(r);
    } catch (err: any) { setServerErr(extractErrorMessage(err)); } finally { setSaving(false); }
  };

  const cell = (l: string, v: string, hi?: boolean, bad?: boolean) => (
    <Box sx={{ flex: 1, minWidth: 92, p: 1.25, borderRadius: 1.5, bgcolor: hi ? alpha(COLOR, 0.08) : 'transparent', border: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block">{l}</Typography>
      <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: bad ? ORANGE : undefined }}>{v}</Typography>
    </Box>
  );
  const isCorr = k === 'correction';
  const vx = num(x);
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="saisie-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="saisie-title" icon={<SaisieIcon />} onClose={onClose} title="Enregistrer la quantité réalisée"
        sub={e ? `${reference ? `${reference} · ` : ''}Étape ${e.ordre} · ${e.designation}${e.travail_label ? ` · ${e.travail_label}` : ''}` : undefined} />
      {isFetching && !e && <LinearProgress />}
      {e && (
        <DialogContent sx={{ pt: '20px !important' }}>
          {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
          {!!plans && (
            <Box sx={{ mb: 1.5 }}>
              <PlansButton count={plans} title={`${reference ? `${reference} · ` : ''}${e.ordre}. ${e.designation}`}
                load={() => comptoirProductionAPI.etapePlans(e.id)} color={COLOR} />
            </Box>
          )}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Machine', e.machine ? `${e.machine.code} — ${e.machine.name}` : '—'],
              ['Responsable', e.responsable?.full_name || 'Non affecté'],
              ['Saisi par', user?.full_name || '—'],
            ].map(([l, v]) => (
              <Grid item xs={6} md={3} key={l}>
                <Typography variant="caption" color="text.secondary">{l}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
              </Grid>
            ))}
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">État actuel</Typography>
              <StatutChip e={e} />
              {e.attente?.motif && <Typography variant="caption" color="error" display="block">{e.attente.motif}{e.attente.detail ? ` — ${e.attente.detail}` : ''}</Typography>}
            </Grid>
          </Grid>
          {e.inconnue && (
            <Msg kind="warn">Cette étape a commencé avant le suivi des quantités. Indiquez d'abord la <b>situation actuelle</b> : ce qui est déjà fait ne doit pas compter pour zéro.</Msg>
          )}
          {!e.defined && (
            <NumberField label="Quantité demandée pour cette étape" required value={dem} onChange={setDem} unit={e.travail_unite || undefined}
              helperText="À définir une seule fois pour cette étape" sx={{ mb: 2, maxWidth: 300 }} />
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {cell('Demandée', ev.demandee > 0 ? fq(ev.demandee) : '—')}
            {cell('Déjà réalisée', fq(e.realisee))}
            {cell('Cette saisie', ev.typed ? `${isCorr && vx < 0 ? '' : '+'}${fq(vx)}` : '—', true)}
            {cell('Conformes', fq(ev.conf))}
            {cell('À refaire', fq(ev.refaire), false, ev.refaire > 0)}
            {cell('Restante', fq(ev.reste), true)}
          </Stack>
          {e.disponible.depuis && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {e.disponible.inconnue
                ? `Étape précédente « ${e.disponible.depuis.designation} » : quantités pas encore saisies.`
                : <>Disponibles depuis « {e.disponible.depuis.designation} » : <b>{fq(e.disponible.limite)}</b> · en attente à cette étape : <b>{fq(Math.max(0, num(e.disponible.limite) - num(e.realisee)))}</b></>}
            </Typography>
          )}

          {kinds.length > 1 && (
            <ToggleButtonGroup exclusive fullWidth size="small" value={k} onChange={(_, v) => v && setK(v)} sx={{ mb: 2 }}>
              {kinds.map((kk) => (
                <ToggleButton key={kk} value={kk} sx={{ textTransform: 'none', fontWeight: 600, '&.Mui-selected': { bgcolor: alpha(COLOR, 0.12), color: COLOR } }}>
                  {KINDS[kk]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {k === 'reprise' ? (
            <NumberField label="Pièces refaites et maintenant conformes" required autoFocus value={x} onChange={setX} sx={{ maxWidth: 320, mb: 2 }}
              helperText={`${fq(e.a_refaire)} pièce(s) à refaire`} />
          ) : isCorr ? (
            <>
              <Msg kind="warn">Une correction s'ajoute à l'historique (elle n'efface aucune saisie). Mettez un signe moins pour retirer : ex -5.</Msg>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} md={3}>
                  <TextField label="Quantité réalisée (±)" fullWidth autoFocus value={x}
                    onChange={(ev2) => /^-?\d*[.,]?\d{0,2}$/.test(ev2.target.value) && setX(ev2.target.value)}
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField label="Non conformes (±)" fullWidth value={nc}
                    onChange={(ev2) => /^-?\d*[.,]?\d{0,2}$/.test(ev2.target.value) && setNc(ev2.target.value)}
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Motif de la correction" required fullWidth value={motif} onChange={(ev2) => setMotif(ev2.target.value)} placeholder="ex : erreur de saisie" />
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6} md={3}>
                <NumberField label={k === 'initial' ? 'Quantité déjà réalisée' : 'Quantité réalisée maintenant'} required fullWidth autoFocus value={x} onChange={setX} />
              </Grid>
              <Grid item xs={6} md={3}>
                <NumberField label="Dont non conformes" fullWidth value={nc} onChange={setNc} helperText="À refaire" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete freeSolo options={meta?.motifs_nc || []} value={motif} inputValue={motif}
                  onInputChange={(_, v, reason) => { if (reason !== 'reset' || v) setMotif(v); }}
                  renderInput={(p) => <TextField {...p} label="Motif de reprise" placeholder="Si non conformes" required={num(nc) > 0} />} />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={5}>
              <TextField label="Date et heure" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={ts} onChange={(ev2) => setTs(ev2.target.value)} />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField label="Observations" fullWidth value={obs} onChange={(ev2) => setObs(ev2.target.value)} placeholder="Facultatif" />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary">Après cette saisie</Typography>
          <ToggleButtonGroup exclusive fullWidth size="small" value={apres} onChange={(_, v) => v && setApres(v)} sx={{ mb: 2, mt: 0.5 }}>
            {APRES.map(([v, l]) => (
              <ToggleButton key={v} value={v} sx={{ textTransform: 'none', fontWeight: 600, '&.Mui-selected': { bgcolor: alpha(v === 'attente' ? RED : COLOR, 0.12), color: v === 'attente' ? RED : COLOR } }}>
                {l}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {apres === 'attente' && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField select label="Motif de l'arrêt" required fullWidth value={attMotif} onChange={(ev2) => setAttMotif(ev2.target.value)}>
                  {(meta?.motifs_attente || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Précision" fullWidth value={attDetail} onChange={(ev2) => setAttDetail(ev2.target.value)} placeholder="ex : tôles inox en commande" />
              </Grid>
            </Grid>
          )}

          {(submitted || ev.typed) && ev.errs.map((t, i) => <Msg key={`e${i}`} kind="error">{t}</Msg>)}
          {ev.warns.map((t, i) => <Msg key={`w${i}`} kind="warn">{t}</Msg>)}
          {ev.done && ev.typed && !ev.errs.length && <Msg kind="ok">La quantité demandée est atteinte : l'étape passera « Terminée ».</Msg>}
          {canEdit && (ev.over || ov) && (
            <FormControlLabel control={<Checkbox checked={ov} onChange={(ev2) => setOv(ev2.target.checked)} />}
              label="Autoriser le dépassement (responsable production)" />
          )}

          {!!e.saisies?.length && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <HistIcon sx={{ color: COLOR, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Historique des saisies</Typography>
                <Typography variant="caption" color="text.secondary">{e.saisies.length} saisie(s) · jamais effacées</Typography>
              </Stack>
              {e.saisies.slice(0, 12).map((s) => (
                <Stack key={s.id} direction="row" spacing={2} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 118, fontFamily: 'monospace' }}>
                    {new Date(s.saisie_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{s.texte}{s.observations ? ` · ${s.observations}` : ''}{s.depassement ? ' · dépassement autorisé' : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.user_name || ''}</Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving || !e} sx={{ ...primaryBtnSx, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- En attente / fin forcée ---------------- */
export const EtapeMotifDialog: React.FC<{
  open: boolean;
  etape: ComptoirEtape | null;
  mode: 'attente' | 'terminer';
  onClose: () => void;
  onSaved: (e: ComptoirEtape) => void;
}> = ({ open, etape, mode, onClose, onSaved }) => {
  const { data: meta } = useComptoirSuiviMeta(open);
  const [motif, setMotif] = useState('');
  const [detail, setDetail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setMotif(mode === 'attente' ? meta?.motifs_attente[0] || '' : ''); setDetail(''); setSubmitted(false); setServerErr(''); }
  }, [open, mode, meta]);

  const err = submitted && !motif.trim() ? (mode === 'attente' ? "Choisissez le motif de l'arrêt." : 'Indiquez pourquoi.') : '';
  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (!etape || !motif.trim()) return;
    setSaving(true);
    try {
      onSaved(await comptoirProductionAPI.statut(etape.id, mode, motif.trim(), detail.trim() || null));
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  const A = mode === 'attente';
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="motif-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="motif-title" icon={A ? <AttenteIcon /> : <DoneIcon />} color={A ? RED : '#2E7D32'} onClose={onClose}
        title={A ? 'Mettre en attente' : "Terminer l'étape"}
        sub={etape ? `Étape ${etape.ordre} · ${etape.designation}` : undefined} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        {A ? (
          <Stack spacing={2}>
            <TextField select label="Motif de l'arrêt" required fullWidth value={motif} onChange={(e) => setMotif(e.target.value)} error={!!err} helperText={err || ' '}>
              {(meta?.motifs_attente || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Précision" fullWidth value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="ex : tôles inox en commande" />
          </Stack>
        ) : (
          <>
            {etape && (
              <Msg kind="warn">
                Seulement <b>{fq(etape.conformes)}/{fq(etape.demandee)}</b> pièces conformes. L'étape sera terminée avec ce qui est fait ;
                le motif est gardé dans l'historique.
              </Msg>
            )}
            <TextField label="Motif" required fullWidth autoFocus value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="ex : le client ne prend que 80 pièces" error={!!err} helperText={err || ' '} sx={{ mt: 1 }} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} color={A ? 'error' : 'success'} sx={{ minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>
          {A ? 'Mettre en attente' : 'Terminer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
